// Asset Maintenance Log Component
// Professional dashboard showing maintenance history for an asset

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { KpiCard } from '@/components/kpi-card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { 
  Loader2, 
  Plus, 
  Trash2, 
  Calendar, 
  Coins, 
  Wrench, 
  FileText,
  Eye,
  Building2,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Asset } from '@/types/asset';
import type { MaintenanceTransaction, MaintenanceSummary } from '@/types/maintenance';
import { maintenanceService } from '@/services/maintenance-service';
import { AddMaintenancePanel } from './AddMaintenancePanel';

interface AssetMaintenanceLogProps {
  asset: Asset;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}

const TYPE_COLORS: Record<string, string> = {
  Service: 'bg-blue-500',
  Repair: 'bg-red-500',
  Inspection: 'bg-green-500',
  Calibration: 'bg-purple-500',
  Cleaning: 'bg-cyan-500',
  Upgrade: 'bg-orange-500',
  Replacement: 'bg-pink-500',
  Other: 'bg-gray-500',
};

export function AssetMaintenanceLog({
  asset,
  open,
  onOpenChange,
  onClose,
}: Readonly<AssetMaintenanceLogProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState<MaintenanceTransaction[]>([]);
  const [summary, setSummary] = useState<MaintenanceSummary | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [viewTransaction, setViewTransaction] = useState<MaintenanceTransaction | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, asset.id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [transactionsData, summaryData] = await Promise.all([
        maintenanceService.getByAssetId(asset.id),
        maintenanceService.getSummaryByAssetId(asset.id),
      ]);
      setTransactions(transactionsData);
      setSummary(summaryData);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load maintenance data';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      await maintenanceService.delete(deleteId);
      toast.success('Maintenance record deleted');
      setDeleteId(null);
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete maintenance record';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
    if (onClose) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const warrantyCount = transactions.filter((t) => t.warrantyWork).length;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-[1000px] flex flex-col p-0" hideClose>
          <SheetHeader className="px-6 pt-6 pb-4 bg-white dark:bg-gray-950 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-xl">
                <FileText className="h-5 w-5" />
                Maintenance Log
              </SheetTitle>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => setShowAddPanel(true)} 
                  size="sm"
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Record
                </Button>
                <Button 
                  onClick={handleClose} 
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-6 space-y-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* KPI Statistics */}
                  {summary && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <KpiCard
                        title="Total Cost"
                        value={formatCurrency(summary.totalGross)}
                        description="Total maintenance spend"
                        icon={Coins}
                      />
                      <KpiCard
                        title="Total Records"
                        value={summary.totalEvents}
                        description="Maintenance events logged"
                        icon={Wrench}
                      />
                      <KpiCard
                        title="Warranty Work"
                        value={warrantyCount}
                        description="Covered by warranty"
                        icon={FileText}
                      />
                      <KpiCard
                        title="Last Logged"
                        value={summary.lastMaintenanceDate ? formatDate(summary.lastMaintenanceDate) : "—"}
                        description="Most recent maintenance"
                        icon={Calendar}
                      />
                    </div>
                  )}

                  <Separator />

                  {/* Maintenance History */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Maintenance History
                        {transactions.length > 0 && (
                          <span className="ml-2 text-foreground">({transactions.length})</span>
                        )}
                      </h3>
                    </div>

                    {transactions.length === 0 ? (
                      <div className="border border-dashed rounded-lg py-12 text-center">
                        <Wrench className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground font-medium">No maintenance records yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Click "Add Record" to create the first entry
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {transactions.map((transaction) => {
                          // Get the type color for subtle gradient background
                          const typeColorClass = TYPE_COLORS[transaction.maintenanceType] || TYPE_COLORS.Other;
                          const gradientMap: Record<string, string> = {
                            'bg-blue-500': 'bg-gradient-to-br from-blue-50/50 via-white to-white dark:from-blue-950/10 dark:via-slate-950 dark:to-slate-950',
                            'bg-green-500': 'bg-gradient-to-br from-green-50/50 via-white to-white dark:from-green-950/10 dark:via-slate-950 dark:to-slate-950',
                            'bg-amber-500': 'bg-gradient-to-br from-amber-50/50 via-white to-white dark:from-amber-950/10 dark:via-slate-950 dark:to-slate-950',
                            'bg-red-500': 'bg-gradient-to-br from-red-50/50 via-white to-white dark:from-red-950/10 dark:via-slate-950 dark:to-slate-950',
                            'bg-purple-500': 'bg-gradient-to-br from-purple-50/50 via-white to-white dark:from-purple-950/10 dark:via-slate-950 dark:to-slate-950',
                            'bg-slate-500': 'bg-gradient-to-br from-slate-50/50 via-white to-white dark:from-slate-900/10 dark:via-slate-950 dark:to-slate-950',
                          };
                          const gradientClass = gradientMap[typeColorClass] || 'bg-gradient-to-br from-slate-50/50 via-white to-white dark:from-slate-900/10 dark:via-slate-950 dark:to-slate-950';
                          
                          return (
                            <div
                              key={transaction.id}
                              className={`group relative border border-slate-200 dark:border-slate-800 rounded-lg hover:shadow-md transition-all duration-200 ${gradientClass}`}
                            >
                              {/* Card Header - Date, Type, and Cost */}
                              <div className="flex items-start justify-between gap-4 p-4 pb-3">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  {/* Date Badge */}
                                  <div className="flex-shrink-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-center min-w-[70px]">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      {format(new Date(transaction.maintenanceDate), 'MMM')}
                                    </div>
                                    <div className="text-xl font-bold text-foreground">
                                      {format(new Date(transaction.maintenanceDate), 'dd')}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {format(new Date(transaction.maintenanceDate), 'yyyy')}
                                    </div>
                                  </div>

                                  {/* Main Content */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="font-semibold text-base text-foreground">
                                        {transaction.reference}
                                      </div>
                                      <Badge
                                        className={`${typeColorClass} text-white text-xs flex-shrink-0`}
                                      >
                                        {transaction.maintenanceType}
                                      </Badge>
                                      {transaction.warrantyWork && (
                                        <Badge variant="outline" className="text-xs flex-shrink-0">
                                          Warranty
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                      {transaction.description}
                                    </p>
                                  </div>
                                </div>

                                {/* Cost */}
                                <div className="flex-shrink-0 text-right">
                                  <div className="text-lg font-bold text-foreground">
                                    {formatCurrency(transaction.gross)}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {transaction.currency || 'GBP'}
                                  </div>
                                </div>
                              </div>

                              {/* Card Footer - Supplier and Actions */}
                              <div className="flex items-center justify-between gap-4 px-4 pb-4 pt-2 border-t border-slate-200 dark:border-slate-800">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                                  <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span className="truncate">{transaction.supplierName || 'No supplier'}</span>
                                  {transaction.supplierReference && (
                                    <>
                                      <span className="text-muted-foreground/50">•</span>
                                      <span className="text-xs truncate">
                                        {transaction.supplierReference}
                                      </span>
                                    </>
                                  )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setViewTransaction(transaction)}
                                    className="h-8 px-3 text-xs gap-1.5"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteId(transaction.id)}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Transaction Details Dialog */}
      <Dialog open={!!viewTransaction} onOpenChange={() => setViewTransaction(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Maintenance Record Details
            </DialogTitle>
          </DialogHeader>
          
          {viewTransaction && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Reference</div>
                    <div className="font-medium">{viewTransaction.reference}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Maintenance Date</div>
                    <div className="font-medium">{formatDate(viewTransaction.maintenanceDate)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Type</div>
                    <div>
                      <Badge
                        className={`${
                          TYPE_COLORS[viewTransaction.maintenanceType] || TYPE_COLORS.Other
                        } text-white`}
                      >
                        {viewTransaction.maintenanceType}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Warranty Work</div>
                    <div className="font-medium">
                      {viewTransaction.warrantyWork ? (
                        <Badge variant="outline" className="text-xs">Yes</Badge>
                      ) : (
                        'No'
                      )}
                    </div>
                  </div>
                </div>
                {viewTransaction.description && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Description</div>
                    <div className="text-sm bg-slate-50 dark:bg-slate-900 p-3 rounded-md">
                      {viewTransaction.description}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Supplier Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Supplier Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Supplier Name</div>
                    <div className="font-medium">{viewTransaction.supplierName || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Supplier Invoice/Reference</div>
                    <div className="font-medium">{viewTransaction.supplierReference || '-'}</div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Cost Breakdown */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Coins className="h-4 w-4" />
                  Cost Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Net (Cost excl. tax)</div>
                    <div className="font-medium">{formatCurrency(viewTransaction.net)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Tax Rate</div>
                    <div className="font-medium">{viewTransaction.taxRate}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Tax Amount</div>
                    <div className="font-medium">{formatCurrency(viewTransaction.tax)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Gross Total (Cost incl. tax)</div>
                    <div className="font-semibold text-lg">{formatCurrency(viewTransaction.gross)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Currency</div>
                    <div className="font-medium">{viewTransaction.currency}</div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Work Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Work Details
                </h3>
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Work Performed
                  </div>
                  <div className="text-sm bg-slate-50 dark:bg-slate-900 p-3 rounded-md whitespace-pre-wrap min-h-[60px]">
                    {viewTransaction.workPerformed || <span className="text-slate-400 italic">No work performed details</span>}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Parts Replaced
                  </div>
                  <div className="text-sm bg-slate-50 dark:bg-slate-900 p-3 rounded-md whitespace-pre-wrap min-h-[60px]">
                    {viewTransaction.partsReplaced || <span className="text-slate-400 italic">No parts replaced</span>}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Additional Notes
                  </div>
                  <div className="text-sm bg-slate-50 dark:bg-slate-900 p-3 rounded-md whitespace-pre-wrap min-h-[60px]">
                    {viewTransaction.notes || <span className="text-slate-400 italic">No additional notes</span>}
                  </div>
                </div>
              </div>

              {/* Scheduling */}
              {viewTransaction.nextMaintenanceDate && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Scheduling
                    </h3>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Next Maintenance Date</div>
                      <div className="font-medium">{formatDate(viewTransaction.nextMaintenanceDate)}</div>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Metadata */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Record Information
                </h3>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Created: {formatDate(viewTransaction.createdDate)} by {viewTransaction.createdBy}</div>
                  {viewTransaction.modifiedDate && viewTransaction.modifiedBy && (
                    <div>Last Modified: {formatDate(viewTransaction.modifiedDate)} by {viewTransaction.modifiedBy}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewTransaction(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Panel */}
      <AddMaintenancePanel
        asset={asset}
        open={showAddPanel}
        onOpenChange={setShowAddPanel}
        onSuccess={() => {
          setShowAddPanel(false);
          loadData();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Maintenance Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this maintenance record? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
